/**
 * Bowl generator — a parametric hollow frustum shell.
 *
 * The same parameter set spans the full range from a dog bowl (wider base
 * than rim) through a standard mixing bowl, a straight-sided bucket, and a
 * tall cup, controlled by two independent diameter sliders.
 *
 *   rimDiameter > baseDiameter  →  classic bowl / mixing bowl
 *   rimDiameter ≈ baseDiameter  →  straight bucket / cup / can
 *   rimDiameter < baseDiameter  →  dog bowl / plant pot / footed vessel
 *
 * Geometry is delegated to `buildFrustumShell` in `generatorGeom.ts`, which
 * produces an outward-facing hollow frustum shell (outer wall + inner wall +
 * solid base + rim annulus) with auto-smooth normals. The bowl is output as a
 * single mesh so it can be smoothed, subdivided, and textured as one piece.
 *
 * When `hasLiquid` is enabled, a flat disc is concatenated into the same mesh
 * at the specified fill level, sized to fit the inner wall at that height.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 */

import {
  buildDisc,
  buildFrustumShell,
  concatPrimitiveGeometries,
  evalFrustumProfile,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BowlParams extends GeneratorParams {
  rimDiameter: number;
  baseDiameter: number;
  height: number;
  profile: number;
  wallThickness: number;
  baseThickness: number;
  segments: number;
  loops: number;
  hasLiquid: boolean;
  liquidFill: number;
}

const BOWL_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "bowl-shape",
    label: "Shape",
    params: [
      {
        id: "rimDiameter",
        label: "Rim Diameter",
        type: "number",
        unit: "meters",
        default: 0.16,
        min: 0.04,
        max: 1.0,
        step: 0.01,
        description: "Outer diameter at the opening.",
      },
      {
        id: "baseDiameter",
        label: "Base Diameter",
        type: "number",
        unit: "meters",
        default: 0.08,
        min: 0.02,
        max: 1.0,
        step: 0.01,
        description:
          "Outer diameter at the base. Larger than rim → dog bowl / pot; equal → bucket / cup.",
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.07,
        min: 0.01,
        max: 0.6,
        step: 0.005,
      },
      {
        id: "profile",
        label: "Profile",
        type: "number",
        default: 0,
        min: -1,
        max: 1,
        step: 0.05,
        description: "−1 concave · 0 linear · +1 convex",
      },
    ],
  },
  {
    type: "section",
    id: "bowl-wall",
    label: "Wall",
    params: [
      {
        id: "wallThickness",
        label: "Wall Thickness",
        type: "number",
        unit: "meters",
        default: 0.004,
        min: 0.001,
        max: 0.05,
        step: 0.001,
      },
      {
        id: "baseThickness",
        label: "Base Thickness",
        type: "number",
        unit: "meters",
        default: 0.006,
        min: 0.001,
        max: 0.05,
        step: 0.001,
      },
    ],
  },
  {
    type: "section",
    id: "bowl-liquid",
    label: "Liquid",
    params: [
      {
        id: "hasLiquid",
        label: "Liquid",
        type: "boolean",
        default: false,
      },
      {
        id: "liquidFill",
        label: "Fill Level",
        type: "number",
        default: 0.7,
        min: 0.0,
        max: 1.0,
        step: 0.01,
        description:
          "Liquid level as a fraction of the interior depth (0 = floor · 1 = rim).",
        showIf: { id: "hasLiquid", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "bowl-resolution",
    label: "Resolution",
    defaultCollapsed: true,
    params: [
      {
        id: "segments",
        label: "Segments",
        type: "number",
        unit: "count",
        default: 32,
        min: 8,
        max: 128,
        step: 4,
      },
      {
        id: "loops",
        label: "Loops",
        type: "number",
        unit: "count",
        default: 0,
        min: 0,
        max: 16,
        step: 1,
        description: "Horizontal ring cuts between base and rim.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildBowl(
  p: BowlParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const rimD = numParam(p, "rimDiameter", 0.16);
  const baseD = numParam(p, "baseDiameter", 0.08);
  const h = numParam(p, "height", 0.07);
  const prof = numParam(p, "profile", 0);
  const wt = numParam(p, "wallThickness", 0.004);
  const bt = numParam(p, "baseThickness", 0.006);
  const segs = intParam(p, "segments", 32);
  const loops = intParam(p, "loops", 0);
  const hasLiquid = boolParam(p, "hasLiquid", false);
  const liquidFill = numParam(p, "liquidFill", 0.7);

  const bowlGeometry = buildFrustumShell(
    rimD * 0.5, // topRadius
    baseD * 0.5, // bottomRadius
    h,
    wt,
    bt,
    segs,
    loops,
    prof,
  );

  if (!hasLiquid) {
    return { kind: "mesh", geometry: bowlGeometry };
  }

  // Compute the inner wall radius at the fill level via the same profile curve
  // as the shell, so the disc sits flush against the inner wall at any taper.
  const interiorDepth = Math.max(0, h - bt);
  const liquidZ = bt + Math.max(0, Math.min(1, liquidFill)) * interiorDepth;
  const liquidT = h > 0 ? liquidZ / h : 0;
  const outerRAtLevel = evalFrustumProfile(
    liquidT,
    baseD * 0.5,
    rimD * 0.5,
    h,
    prof,
  );
  const innerRAtLevel = Math.max(0.001, outerRAtLevel - wt);

  const geometry = concatPrimitiveGeometries([
    bowlGeometry,
    transformGeometry(buildDisc(innerRAtLevel, segs), [0, 0, liquidZ]),
  ]);

  return { kind: "mesh", geometry };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BOWL_GENERATOR: Generator = defineGenerator<BowlParams>({
  id: "bowl",
  label: "Bowl",
  category: "prop",
  paramSpecs: BOWL_SPECS,
  defaultParams: paramsFromSpecs(BOWL_SPECS) as BowlParams,
  build: buildBowl,
});
